import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import {
  GfFlyerItem,
  RecommendationEnginePreviewItemDto,
} from "@swagger/typescript-fetch-goflyer";
import InfoTooltip from "./InfoTooltip";
import { SIGNAL_CONCEPT_TOOLTIPS } from "../copy";
import { engineTokens } from "../theme";

export type PreviewItem = {
  flyerItemId: string;
  finalScore: number;
  matchedSources?: string[];
  signals?: Record<string, number>;
  item?: GfFlyerItem;
};

const pickThumbnailSrc = (
  item: GfFlyerItem | undefined,
): string | undefined => {
  if (!item) return undefined;
  const direct = item.gfImages?.find((image) => image?.src)?.src;
  if (direct) return direct;
  const multi = item.gfMultiImages?.find((m) => m?.gfImage?.src)?.gfImage?.src;
  return multi;
};

const pickName = (item: GfFlyerItem | undefined, fallback: string): string => {
  if (!item) return fallback;
  return item.name || item.nameChinese || item.nameFrench || fallback;
};

/** Reads the preview response rows, which share one shape across previews. */
export const toPreviewItems = (
  entries: RecommendationEnginePreviewItemDto[] | undefined,
): PreviewItem[] =>
  Array.isArray(entries)
    ? entries.map((entry) => ({
        flyerItemId: entry.flyerItemId,
        finalScore: entry.finalScore,
        matchedSources: Array.isArray(entry.matchedSources)
          ? entry.matchedSources.map((source) => String(source))
          : [],
        signals: entry.signals ?? {},
        item: entry.item,
      }))
    : [];

type Props = {
  items: PreviewItem[];
};

/** Previewed items with their score, matched sources and signals. */
const PreviewResultsTable = ({ items }: Props) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>#</TableCell>
          <TableCell>Item</TableCell>
          <TableCell>
            <Box sx={{ display: "inline-flex", alignItems: "center" }}>
              Matched sources
              <InfoTooltip title={SIGNAL_CONCEPT_TOOLTIPS.matchedSources} />
            </Box>
          </TableCell>
          <TableCell align="right">
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              Score
              <InfoTooltip title={SIGNAL_CONCEPT_TOOLTIPS.finalScore} />
            </Box>
          </TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((row, index) => {
          const thumbSrc = pickThumbnailSrc(row.item);
          const name = pickName(row.item, row.flyerItemId);
          return (
            <React.Fragment key={row.flyerItemId}>
              <TableRow>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      variant="rounded"
                      src={thumbSrc}
                      alt={name}
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: engineTokens.bgMuted,
                        color: engineTokens.textMuted,
                        objectFit: "contain",
                      }}
                    >
                      <ImageNotSupportedOutlinedIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                        noWrap
                        title={name}
                      >
                        {name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {row.flyerItemId}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {(row.matchedSources ?? []).map((source) => (
                      <Chip
                        key={source}
                        size="small"
                        label={source}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {row.finalScore?.toFixed(4)}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    onClick={() =>
                      setExpandedId(
                        expandedId === row.flyerItemId ? null : row.flyerItemId,
                      )
                    }
                  >
                    Signals
                  </Button>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                  <Collapse in={expandedId === row.flyerItemId}>
                    <Box sx={{ pl: 2, pb: 1 }}>
                      {Object.entries(row.signals ?? {}).map(([k, v]) => (
                        <Typography key={k} variant="caption" display="block">
                          {k}: {String(v)}
                        </Typography>
                      ))}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default PreviewResultsTable;
